import React, { type ReactElement, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Columns3, List } from 'lucide-react';
import { Button, Empty, Input, Skeleton, Tag, Tooltip, Typography } from 'antd';
import { ArrowRightOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table/interface';
import { Table } from 'antd';

import { useTranslation } from '@govard/i18n-core/react';
import { useDataViewPagination } from '../../../shared/dataview/useDataViewPagination.js';
import type { HotLeadItem, HotLeadReason } from '../api/hot-leads.client.js';
import { useHotLeads } from '../hooks/useHotLeads.js';

const { Text } = Typography;

type FilterValue = 'all' | HotLeadReason;
type StageFilterValue = 'all' | string;

const STAGE_COLORS: Record<string, string> = {
  New: 'blue',
  Contacted: 'orange',
  Negotiation: 'gold',
  Won: 'green',
  Lost: 'red',
  new: 'blue',
  contacted: 'orange',
  negotiation: 'gold',
  won: 'green',
  lost: 'red',
};

const FILTER_OPTIONS: readonly FilterValue[] = ['all', 'stale', 'due', 'high_value'];

export function HotLeadsPage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterValue>('all');
  const [stageFilter, setStageFilter] = useState<StageFilterValue>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const query = useHotLeads(filter === 'all' ? {} : { filter });

  const rawLeads = query.data ?? [];

  const availableStages = useMemo(() => {
    const stages = new Set<string>();
    for (const lead of rawLeads) {
      stages.add(lead.deal.stage);
    }
    return Array.from(stages).sort((a, b) => a.localeCompare(b));
  }, [rawLeads]);

  const visibleLeads = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return rawLeads.filter((lead) => {
      if (stageFilter !== 'all' && lead.deal.stage !== stageFilter) return false;
      if (normalizedQuery.length === 0) return true;

      const haystack = [
        lead.deal.title,
        lead.deal.contactName ?? '',
        lead.deal.stage,
        t(`crm.hot_leads.reason.${lead.primaryReason}`),
      ].join(' ').toLowerCase();

      return haystack.includes(normalizedQuery);
    });
  }, [rawLeads, searchQuery, stageFilter, t]);
  const pagination = useDataViewPagination(visibleLeads);

  const columns: ColumnsType<HotLeadItem> = [
    {
      title: t('crm.hot_leads.table_header.deal'),
      key: 'deal',
      render: (_: unknown, record) => (
        <Text strong style={{ fontSize: 13, color: '#44403c' }}>
          {record.deal.title}
        </Text>
      ),
    },
    {
      title: t('crm.hot_leads.table_header.contact'),
      dataIndex: ['deal', 'contactName'],
      key: 'contact',
      render: (name: string | null) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {name ?? <Text disabled>—</Text>}
        </Text>
      ),
    },
    {
      title: t('crm.hot_leads.table_header.stage'),
      dataIndex: ['deal', 'stage'],
      key: 'stage',
      align: 'center',
      render: (stage: string) => (
        <Tag color={STAGE_COLORS[stage] ?? 'default'} style={{ margin: 0 }}>
          {stage}
        </Tag>
      ),
    },
    {
      title: 'Сумма',
      dataIndex: ['deal', 'amount'],
      key: 'amount',
      render: (amount: number | string, record) => (
        <Text style={{ color: '#78716c', fontFamily: 'monospace', fontWeight: 600 }}>
          {typeof amount === 'number' ? amount.toLocaleString('ru-RU') : amount} {record.deal.currency}
        </Text>
      ),
    },
    {
      title: t('crm.hot_leads.table_header.reason'),
      dataIndex: 'primaryReason',
      key: 'reason',
      render: (reason: HotLeadReason) => (
        <Tag style={{ fontSize: 11, margin: 0 }}>
          {t(`crm.hot_leads.reason.${reason}`)}
        </Tag>
      ),
    },
    {
      title: 'Скор',
      dataIndex: 'score',
      key: 'score',
      align: 'center',
      render: (score: number) => (
        <Text style={{ color: '#d97706', fontFamily: 'monospace', fontWeight: 600 }}>
          {score}
        </Text>
      ),
    },
    {
      title: t('crm.hot_leads.table_header.action'),
      key: 'action',
      align: 'center',
      render: (_: unknown, record) => (
        <Button
          data-testid={`hotleads-action-${record.deal.id}`}
          size="small"
          icon={<ArrowRightOutlined />}
          onClick={(event) => {
            event.stopPropagation();
            navigate(`/deals/${record.deal.id}?tab=chat`);
          }}
        >
          Открыть
        </Button>
      ),
    },
  ];

  return (
    <div className="animate-fade-in flex h-full flex-col gap-6">
      <header className="z-10 flex w-full shrink-0 items-center justify-between">
        <div>
          <h1 className="m-0 mb-1 font-bold leading-tight tracking-tight" style={{ color: '#2d2418', fontSize: 28 }}>
            {t('crm.hot_leads.title')}
          </h1>
          <Text type="secondary" style={{ display: 'block', fontSize: 13, marginTop: 4 }}>
            <span data-testid="hotleads-visible-count">{visibleLeads.length} из {rawLeads.length}</span> сделок · приоритетный список
          </Text>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center rounded-lg p-1" style={{ background: '#e7e5e4', border: '1px solid #d6d3d1' }}>
            <Tooltip title="Канбан">
              <Button
                type="text"
                size="small"
                aria-label="Канбан"
                disabled
                style={{ minWidth: 32, height: 28, padding: 0, color: '#a8a29e', borderRadius: 6 }}
              >
                <Columns3 size={14} />
              </Button>
            </Tooltip>
            <Tooltip title="Список">
              <Button
                type="text"
                size="small"
                aria-label="Список"
                style={{
                  minWidth: 32,
                  height: 28,
                  padding: 0,
                  backgroundColor: '#fff',
                  color: '#d97706',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  borderRadius: 6,
                }}
              >
                <List size={14} />
              </Button>
            </Tooltip>
          </div>

          <Tooltip title="Обновить">
            <Button
              aria-label="Обновить горячие лиды"
              icon={<ReloadOutlined />}
              onClick={() => void query.refetch()}
              style={{ borderRadius: 8, height: 36, width: 36 }}
            />
          </Tooltip>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-6">
        <div
          className="flex w-full shrink-0 items-center gap-4 rounded-2xl border bg-white px-4 py-3 shadow-sm"
          style={{ borderColor: '#e7e5e4' }}
        >
          <div className="flex-1" style={{ maxWidth: 400 }}>
            <Input
              id="hotleads-search"
              aria-label="Поиск горячих лидов"
              placeholder="Поиск по сделке или контакту"
              value={searchQuery}
              onChange={(event) => {
                setSearchQuery(event.target.value);
                pagination.resetPage();
              }}
              variant="borderless"
              className="transition-colors"
              style={{ backgroundColor: '#f5f5f4', borderRadius: 8, padding: '8px 12px' }}
              allowClear
            />
          </div>

          <div className="ml-auto flex items-center gap-3">
            <select
              value={filter}
              onChange={(event) => {
                setFilter(event.target.value as FilterValue);
                pagination.resetPage();
              }}
              aria-label="Фильтр по причине"
              className="bg-transparent font-medium transition-colors"
              style={{
                height: 36,
                border: '1px solid #e7e5e4',
                borderRadius: 8,
                padding: '0 12px',
                color: '#44403c',
                fontSize: 14,
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              {FILTER_OPTIONS.map((item) => (
                <option key={item} value={item}>
                  {t(`crm.hot_leads.filter.${item}`)}
                </option>
              ))}
            </select>

            <select
              value={stageFilter}
              onChange={(event) => {
                setStageFilter(event.target.value);
                pagination.resetPage();
              }}
              aria-label="Фильтр по стадии"
              className="bg-transparent font-medium transition-colors"
              style={{
                height: 36,
                border: '1px solid #e7e5e4',
                borderRadius: 8,
                padding: '0 12px',
                color: '#44403c',
                fontSize: 14,
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="all">Все стадии</option>
              {availableStages.map((stage) => (
                <option key={stage} value={stage}>{stage}</option>
              ))}
            </select>
          </div>
        </div>

        <div
          className="min-h-0 flex-1"
          data-testid="hotleads-list-view"
          style={{ background: '#fffdfa', border: '1px solid #e7e5e4', borderRadius: 8, overflow: 'hidden' }}
        >
          {query.isLoading ? (
            <div className="flex flex-col gap-3 p-6">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} active paragraph={{ rows: 1 }} />
              ))}
            </div>
          ) : (
            <Table<HotLeadItem>
              dataSource={visibleLeads}
              columns={columns}
              rowKey={(r) => r.deal.id}
              pagination={pagination.pagination}
              size="small"
              data-testid="hotleads-table"
              onRow={(record) => ({
                'data-testid': `hotleads-row-${record.deal.id}`,
                style: { cursor: 'pointer' },
                onClick: () => navigate(`/deals/${record.deal.id}?tab=chat`),
                onMouseEnter: (event) => {
                  event.currentTarget.style.backgroundColor = '#fffbeb';
                },
                onMouseLeave: (event) => {
                  event.currentTarget.style.backgroundColor = 'transparent';
                },
              } as React.HTMLAttributes<HTMLElement>)}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={<Text type="secondary">Нет горячих лидов по текущим фильтрам</Text>}
                  />
                ),
              }}
              style={{ fontSize: 13 }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
